import { useState, useContext, useEffect } from 'react';
import {
  Box, Button, Chip, CircularProgress, Divider, FormControl, IconButton, InputLabel, MenuItem,
  Select, Stack, Step, StepLabel, Stepper, TextField, Tooltip, Typography,
} from '@mui/material';
import { AddOutlined, DeleteOutline } from '@mui/icons-material';
import { DialogWrapper } from '../../presentation-components/display-elements-components/dialog-wrapper';
import { BackButtonLink } from '../../presentation-components/display-elements-components/back-button-link';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import SurveyAdminService from '../../../shared/services/survey/survey-admin.service';
import { CalcQuestionSelect } from '../../presentation-components/surveys/builder/calc-question-select';
import { ConversionFactorPicker, ConversionSelection } from '../../presentation-components/surveys/builder/conversion-factor-picker';
import { useConversionYearFromReportingSelection } from '../../presentation-components/surveys/builder/use-conversion-year';
import {
  LINEAR_OPS,
  LinearFormula,
  LinearFormulaStep,
  LinearOp,
  extractAliasRefs,
  linearToTree,
} from '../../../shared/utils/survey-rules/formula';
import { defaultPathForQuestion } from '../../../shared/utils/survey-rules/question-eligibility';
import {
  ICreateEmissionRule, ISurveyQuestionGroupItem, ISurveyQuestionOption,
} from '../../../shared/services/survey/survey.model';

function cartesian(arrays: string[][]): string[][] {
  if (arrays.length === 0) return [[]];
  const [first, ...rest] = arrays;
  const restProd = cartesian(rest);
  return first.flatMap(v => restProd.map(r => [v, ...r]));
}

interface CombinationRow {
  conditions: string[];
  selection: ConversionSelection | null;
  /** Seeds the factor picker when the row opens (copy-from-previous or reopening). */
  seedConversionId: string | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  successHandler: () => void;
  groupId: string;
  groupItems: ISurveyQuestionGroupItem[];
  adjustmentAliases: string[];
  // The partner Library authors the same combinations against its own tables. The cartesian
  // product, the option pickers and the factor cascade are identical, so injecting the one
  // save call is the whole of the difference.
  api?: { createEmissionRule: (groupId: string, payload: ICreateEmissionRule) => Promise<unknown> };
}

/**
 * Bulk create: one rule per combination of up to three choice questions' options,
 * each with its own emission factor. Saves ordinary rules; nothing about them is
 * special afterwards.
 */
export const BulkRuleCreateDialog = ({ open, onClose, successHandler, groupId, groupItems, adjustmentAliases, api }: Props) => {
  const surveyAdminService = SurveyAdminService.getInstance();
  const userNotificationsContext = useContext(UserNotificationsContext);
  const reportingYear = useConversionYearFromReportingSelection();

  const [step, setStep] = useState(0);
  const [baseName, setBaseName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [valueQuestionId, setValueQuestionId] = useState('');
  const [valuePath, setValuePath] = useState<string>('numericValue');
  const [formula, setFormula] = useState<LinearFormula>({ startKind: 'alias', startAlias: 'qty', startLiteral: undefined, steps: [] });
  const [conditionCols, setConditionCols] = useState<{ questionId: string }[]>([{ questionId: '' }]);
  const [rows, setRows] = useState<CombinationRow[]>([]);
  const [activeRowIdx, setActiveRowIdx] = useState<number | null>(null);

  useEffect(() => {
    if (!open) return;
    setStep(0); setBaseName(''); setValueQuestionId(''); setValuePath('numericValue');
    setFormula({ startKind: 'alias', startAlias: 'qty', startLiteral: undefined, steps: [] });
    setConditionCols([{ questionId: groupItems[0]?.surveyQuestionId ?? '' }]);
    setRows([]); setActiveRowIdx(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const optionsFor = (qid: string): ISurveyQuestionOption[] =>
    groupItems.find(i => i.surveyQuestionId === qid)?.surveyQuestion?.surveyQuestionOptions ?? [];

  const generateRows = () => {
    const arrays = conditionCols.map(c => {
      const opts = optionsFor(c.questionId);
      return opts.length > 0 ? opts.map(o => o.id) : [''];
    });
    const combos = cartesian(arrays);
    setRows(combos.map(conditions => ({ conditions, selection: null, seedConversionId: null })));
  };

  const setRowSelection = (ri: number, selection: ConversionSelection | null) => {
    setRows(prev => prev.map((r, j) => (j === ri ? { ...r, selection } : r)));
  };

  const lastSetSelection = [...rows].reverse().find(r => r.selection)?.selection ?? null;

  const handleNext = () => {
    if (step === 1) generateRows();
    setStep(s => s + 1);
  };

  const handleSave = async () => {
    if (!rows.some(r => r.selection)) {
      userNotificationsContext.openSnackbar({ open: true, message: 'Set a conversion factor for at least one row.', backgroundColor: 'text.primary' });
      return;
    }
    setIsSubmitting(true);
    try {
      const valueInput = { alias: 'qty', kind: 'question' as const, questionId: valueQuestionId, path: valuePath };
      const formulaTree = linearToTree(formula);
      const usedAdjustmentAliases = [...new Set(
        extractAliasRefs(formulaTree).filter(a => a !== 'qty' && adjustmentAliases.includes(a)),
      )];
      const adjustmentInputs = usedAdjustmentAliases.map(alias => ({ alias, kind: 'adjustmentRef' as const }));

      for (const row of rows) {
        if (!row.selection) continue;
        const conditions = conditionCols
          .map((col, ci) => ({ questionId: col.questionId, op: 'equals' as const, value: row.conditions[ci] ?? '' }))
          .filter(c => c.questionId && c.value);
        const answerLabels = conditionCols
          .map((col, ci) => optionsFor(col.questionId).find(o => o.id === row.conditions[ci])?.label ?? row.conditions[ci] ?? '')
          .filter(Boolean)
          .join(' x ');
        const ruleName = answerLabels ? `${baseName.trim()} - ${answerLabels}` : baseName.trim();
        const payload: ICreateEmissionRule = {
          name: ruleName,
          inputs: [valueInput, ...adjustmentInputs],
          formula: formulaTree,
          conversionId: row.selection.conversionId,
          measurementUnitId: row.selection.measurementUnitId || undefined,
          groupId: row.selection.groupId || null,
          scopeMetadata: conditions.length > 0 ? { applyWhen: { allOf: conditions } } : null,
        };
        await (api?.createEmissionRule ?? ((g: string, pl: ICreateEmissionRule) => surveyAdminService.createEmissionRule(g, pl)))(groupId, payload);
      }
      const createdCount = rows.filter(r => r.selection).length;
      userNotificationsContext.openSnackbar({ open: true, message: `${createdCount} rule${createdCount > 1 ? 's' : ''} created`, backgroundColor: 'primary.dark' });
      successHandler();
      onClose();
    } catch {
      userNotificationsContext.openSnackbar({ open: true, message: 'Failed to create rules.', backgroundColor: 'text.primary' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateStep = (i: number, patch: Partial<LinearFormulaStep>) =>
    setFormula(f => ({ ...f, steps: f.steps.map((s, j) => j === i ? { ...s, ...patch } : s) }));

  const canNext0 = !!valueQuestionId;
  const canNext1 = conditionCols.length > 0 && conditionCols.every(c => c.questionId);
  const canSave = rows.some(r => r.selection);

  return (
    <DialogWrapper
      open={open}
      onClose={onClose}
      title='Bulk Create Rules'
      cancelHandler={onClose}
      submitHandler={step < 2 ? handleNext : handleSave}
      isSubmitting={isSubmitting}
      disableSaveButton={step === 0 ? (!canNext0 || !baseName.trim()) : step === 1 ? !canNext1 : !canSave}
      submitButtonText={step < 2
        ? 'Next'
        : `Create ${rows.filter(r => r.selection).length} Rule${rows.filter(r => r.selection).length !== 1 ? 's' : ''}`}
      maxWidth='lg'
    >
      <Stack spacing={3} sx={{ mt: 1 }}>
        <Stepper activeStep={step}>
          {['Amount and formula', 'Condition questions', 'Factors per combination'].map(l => <Step key={l}><StepLabel>{l}</StepLabel></Step>)}
        </Stepper>

        {step > 0 && !isSubmitting && (
          <Box sx={{ alignSelf: 'flex-start' }}>
            <BackButtonLink buttonText='Back' onClick={() => { setStep(s => s - 1); setActiveRowIdx(null); }} />
          </Box>
        )}

        <TextField
          label='Base rule name'
          helperText='Each rule is named with its answer options appended, for example Car travel - Petrol'
          value={baseName}
          onChange={e => setBaseName(e.target.value)}
          fullWidth
          required
        />

        {/* Step 0 */}
        {step === 0 && (
          <Box>
            <Typography variant='subtitle2' sx={{ mb: 1.5 }}>Which question provides the quantity?</Typography>
            <Stack direction='row' spacing={1} sx={{ mb: 2 }} flexWrap='wrap'>
              <CalcQuestionSelect
                value={valueQuestionId}
                groupItems={groupItems}
                onChange={(qid, item) => {
                  setValueQuestionId(qid);
                  setValuePath(defaultPathForQuestion(item));
                }}
                sx={{ minWidth: 260 }}
              />
            </Stack>

            <Typography variant='subtitle2' sx={{ mb: 0.5 }}>Optional: transformation steps before conversion</Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 1.5 }}>Leave empty to pass the answer directly to the factor.</Typography>
            <Stack spacing={1.5}>
              {formula.steps.map((s, i) => (
                <Stack key={i} direction='row' spacing={1} alignItems='center'>
                  <Typography variant='caption' color='text.secondary' sx={{ minWidth: 72 }}>Step {i + 1}</Typography>
                  <FormControl size='small' sx={{ width: 140 }}>
                    <InputLabel>Operation</InputLabel>
                    <Select label='Operation' value={s.op} onChange={e => updateStep(i, { op: e.target.value as LinearOp })}>
                      {LINEAR_OPS.map(op => <MenuItem key={op.value} value={op.value}>{op.label}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <FormControl size='small' sx={{ width: 90 }}>
                    <Select value={s.valueKind} onChange={e => updateStep(i, { valueKind: e.target.value as 'alias' | 'literal' })}>
                      <MenuItem value='alias'>Input</MenuItem>
                      <MenuItem value='literal'>Number</MenuItem>
                    </Select>
                  </FormControl>
                  {s.valueKind === 'literal' ? (
                    <TextField size='small' type='number' label='Value' value={s.literal ?? ''} onChange={e => updateStep(i, { literal: Number(e.target.value) })} sx={{ width: 120 }} inputProps={{ step: 'any' }} />
                  ) : (
                    <FormControl size='small' sx={{ minWidth: 130 }}>
                      <InputLabel>Input</InputLabel>
                      <Select label='Input' value={s.alias} onChange={e => updateStep(i, { alias: e.target.value })}>
                        {adjustmentAliases.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
                      </Select>
                    </FormControl>
                  )}
                  <IconButton size='small' onClick={() => setFormula(f => ({ ...f, steps: f.steps.filter((_, j) => j !== i) }))}>
                    <DeleteOutline fontSize='small' />
                  </IconButton>
                </Stack>
              ))}
              <Button size='small' startIcon={<AddOutlined />} sx={{ alignSelf: 'flex-start' }}
                onClick={() => setFormula(f => ({ ...f, steps: [...f.steps, { op: '÷', valueKind: 'literal', alias: '', literal: 1 }] }))}>
                Add step
              </Button>
            </Stack>
          </Box>
        )}

        {/* Step 1 */}
        {step === 1 && (
          <Box>
            <Typography variant='subtitle2' sx={{ mb: 0.5 }}>Select up to 3 condition questions</Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>The next step generates one row per combination of their answer options.</Typography>
            <Stack spacing={1.5}>
              {conditionCols.map((col, i) => (
                <Stack key={i} direction='row' spacing={1} alignItems='center'>
                  <FormControl size='small' sx={{ flex: 1, maxWidth: 320 }}>
                    <InputLabel>Condition question {i + 1}</InputLabel>
                    <Select label={`Condition question ${i + 1}`} value={col.questionId}
                      onChange={e => setConditionCols(prev => prev.map((c, j) => j === i ? { questionId: e.target.value } : c))}>
                      {groupItems.map(item => (
                        <MenuItem key={item.surveyQuestionId} value={item.surveyQuestionId}>
                          {item.surveyQuestion?.prompt ?? item.surveyQuestionId}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {col.questionId && (
                    <Typography variant='caption' color='text.secondary'>{optionsFor(col.questionId).length} options</Typography>
                  )}
                  {conditionCols.length > 1 && (
                    <IconButton size='small' onClick={() => setConditionCols(prev => prev.filter((_, j) => j !== i))}>
                      <DeleteOutline fontSize='small' />
                    </IconButton>
                  )}
                </Stack>
              ))}
              {conditionCols.length < 3 && (
                <Button size='small' startIcon={<AddOutlined />} sx={{ alignSelf: 'flex-start' }}
                  onClick={() => setConditionCols(prev => [...prev, { questionId: '' }])}>
                  Add condition question
                </Button>
              )}
            </Stack>
          </Box>
        )}

        {/* Step 2 */}
        {step === 2 && (
          <Box>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
              {rows.length} combination{rows.length !== 1 ? 's' : ''} generated. Delete rows you don&apos;t need, then set the conversion factor for each remaining row.
            </Typography>
            <Stack spacing={1.5}>
              {rows.map((row, ri) => (
                <Box key={ri} sx={{ p: 1.5, border: '1px solid', borderColor: row.selection ? 'success.light' : 'divider', borderRadius: 1 }}>
                  <Stack direction='row' spacing={1} alignItems='flex-start' flexWrap='wrap'>
                    <Stack direction='row' spacing={0.5} flexWrap='wrap' sx={{ flex: 1, alignItems: 'center', pt: 0.5 }}>
                      {conditionCols.map((col, ci) => {
                        const prompt = groupItems.find(i => i.surveyQuestionId === col.questionId)?.surveyQuestion?.prompt ?? col.questionId;
                        const optionLabel = optionsFor(col.questionId).find(o => o.id === row.conditions[ci])?.label ?? row.conditions[ci] ?? '—';
                        return <Chip key={ci} label={`${prompt}: ${optionLabel}`} size='small' variant='outlined' />;
                      })}
                    </Stack>
                    {activeRowIdx === ri ? (
                      <Stack spacing={1} sx={{ minWidth: 280, flex: 2 }}>
                        {/* Each open row gets its own picker instance (keyed), so per-row factor
                            state is structural instead of swapped through one shared form. */}
                        <ConversionFactorPicker
                          key={`row-${ri}-${row.seedConversionId ?? 'blank'}`}
                          initialConversionId={row.seedConversionId}
                          calendarYear={reportingYear}
                          onChange={sel => setRowSelection(ri, sel)}
                        />
                        <Button size='small' variant='outlined' onClick={() => setActiveRowIdx(null)}>
                          Done
                        </Button>
                      </Stack>
                    ) : (
                      row.selection ? (
                        <Chip
                          label={[row.selection.conversion.detailLevelOne, row.selection.conversion.detailLevelTwo].filter(Boolean).join(' · ') || 'Factor set'}
                          size='small' color='success' variant='outlined'
                          onClick={() => {
                            setRows(prev => prev.map((r, j) => (j === ri ? { ...r, seedConversionId: r.selection?.conversionId ?? null } : r)));
                            setActiveRowIdx(ri);
                          }}
                        />
                      ) : (
                        <Stack direction='row' spacing={1}>
                          {lastSetSelection && (
                            <Tooltip title='Use the same factor as the last completed row'>
                              <Button
                                size='small'
                                onClick={() => {
                                  setRows(prev => prev.map((r, j) => (j === ri ? { ...r, seedConversionId: lastSetSelection.conversionId } : r)));
                                  setActiveRowIdx(ri);
                                }}
                              >
                                Copy previous factor
                              </Button>
                            </Tooltip>
                          )}
                          <Button
                            size='small'
                            onClick={() => {
                              setRows(prev => prev.map((r, j) => (j === ri ? { ...r, seedConversionId: null } : r)));
                              setActiveRowIdx(ri);
                            }}
                          >
                            Set factor
                          </Button>
                        </Stack>
                      )
                    )}
                    <Tooltip title='Remove row'>
                      <IconButton size='small' onClick={() => {
                        setRows(prev => prev.filter((_, j) => j !== ri));
                        if (activeRowIdx === ri) setActiveRowIdx(null);
                      }}>
                        <DeleteOutline fontSize='small' />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Box>
              ))}
            </Stack>
            {isSubmitting && (
              <Stack direction='row' spacing={1} alignItems='center' sx={{ mt: 2 }}>
                <CircularProgress size={16} />
                <Typography variant='body2' color='text.secondary'>Creating rules…</Typography>
              </Stack>
            )}
          </Box>
        )}

        <Divider />
      </Stack>
    </DialogWrapper>
  );
};
